import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Gift, Check } from 'lucide-react';
import SecurityCheck from '../components/SecurityCheck';

const Register = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [securityVerified, setSecurityVerified] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(email, password, fullName);
      toast.success('Account created! Welcome to TRADERSCAPITAL');
      navigate('/challenges');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Up to $200K in funding",
    "100% profit split",
    "Free retake on first purchase",
    "No time limits"
  ];

  // Show security check first
  if (!securityVerified) {
    return <SecurityCheck onVerified={() => setSecurityVerified(true)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 bg-[hsl(220,20%,4%)] bg-hero-gradient">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md bg-[hsl(220,20%,7%)]/90 backdrop-blur-xl border border-white/10 shadow-2xl shadow-blue-500/10 relative z-10" data-testid="register-card">
        <CardHeader className="text-center pb-2">
          <Badge className="mb-4 mx-auto px-4 py-2 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
            <Gift className="w-4 h-4 mr-2" />
            FREE RETAKE INCLUDED
          </Badge>
          <CardTitle className="font-heading text-3xl uppercase tracking-tight text-white">Get Started</CardTitle>
          <CardDescription className="text-gray-400">Create your account and start trading</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Benefits List */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-xl border border-white/5">
            <div className="grid grid-cols-2 gap-2">
              {benefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-gray-300 font-medium">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                data-testid="register-name"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="trader@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password"
                className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500/20 rounded-lg h-12"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full font-heading uppercase tracking-wider bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-full h-12 shadow-lg shadow-blue-500/25 mt-6"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'CREATE ACCOUNT'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium hover:underline" data-testid="register-login-link">
              Sign In
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
